import { useState, useEffect, useRef } from 'react';
import { MessageSquare, X, Send, Loader2, AlertTriangle, Phone, FileText, Sparkles, RotateCcw } from 'lucide-react';
import api from '@/lib/api';

const SESSION_KEY = 'beaulon_chat_session_id';
const SUGGESTIONS = [
  'Comment obtenir un transport ambulance ?',
  'Quelle est la différence entre VSL et taxi conventionné ?',
  'Le transport est-il remboursé par la Sécu ?',
  'Je veux prendre un RDV pour ma dialyse.',
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  // Restore session from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(SESSION_KEY);
    if (saved) {
      setSessionId(saved);
      // Re-hydrate messages
      api.get(`/chat/${saved}/messages`)
        .then(r => {
          if (r.data?.messages?.length > 0) {
            setMessages(r.data.messages);
          }
        })
        .catch(() => {});
    }
  }, []);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  // Focus input on open
  useEffect(() => {
    if (open) {
      setHasUnread(false);
      setTimeout(() => inputRef.current?.focus(), 250);
    }
  }, [open]);

  const sendMessage = async (text) => {
    const messageText = (text ?? input).trim();
    if (!messageText || loading) return;
    setInput('');
    const userMsg = { role: 'user', content: messageText, created_at: new Date().toISOString(), id: `tmp-${Date.now()}` };
    setMessages(m => [...m, userMsg]);
    setLoading(true);
    try {
      const res = await api.post('/chat', {
        session_id: sessionId,
        message: messageText,
      });
      if (res.data.session_id && res.data.session_id !== sessionId) {
        setSessionId(res.data.session_id);
        localStorage.setItem(SESSION_KEY, res.data.session_id);
      }
      const assistantMsg = {
        role: 'assistant',
        content: res.data.response,
        created_at: new Date().toISOString(),
        id: res.data.message_id,
      };
      setMessages(m => [...m, assistantMsg]);
    } catch (err) {
      const errMsg = {
        role: 'assistant',
        content: "Je suis momentanément indisponible 😔. Vous pouvez nous appeler au **04 70 42 72 63** ou nous écrire via la page Contact.",
        created_at: new Date().toISOString(),
        id: `err-${Date.now()}`,
        isError: true,
      };
      setMessages(m => [...m, errMsg]);
    } finally {
      setLoading(false);
    }
  };

  const resetSession = () => {
    localStorage.removeItem(SESSION_KEY);
    setSessionId(null);
    setMessages([]);
  };

  return (
    <>
      {/* Floating button */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`fixed bottom-20 md:bottom-6 right-4 md:right-6 z-50 h-14 w-14 rounded-full shadow-[0_10px_25px_rgba(15,23,32,0.25)] flex items-center justify-center transition-all hover:scale-105 focus-ring ${
          open
            ? 'bg-anthracite-900 text-white'
            : 'bg-gradient-to-br from-brand-500 to-brand-700 text-white'
        }`}
        aria-label={open ? 'Fermer l\'assistant' : 'Ouvrir l\'assistant'}
        data-testid="chat-widget-toggle"
      >
        {open ? <X className="h-6 w-6" /> : (
          <>
            <MessageSquare className="h-6 w-6" />
            {hasUnread && <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-amber-400 ring-2 ring-white"></span>}
            <span className="absolute inset-0 rounded-full bg-brand-500 opacity-30 animate-pulse-ring pointer-events-none"></span>
          </>
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-36 md:bottom-24 right-2 md:right-6 left-2 md:left-auto z-50 w-auto md:w-[400px] max-w-[100vw] md:max-w-[400px] rounded-2xl bg-white border shadow-2xl flex flex-col overflow-hidden animate-fade-up"
          style={{ maxHeight: 'min(640px, calc(100vh - 12rem))' }}
          role="dialog"
          aria-label="Assistant Beaulon"
          data-testid="chat-widget-panel"
        >
          {/* Header */}
          <header className="bg-gradient-to-r from-brand-700 to-brand-900 text-white px-4 py-3 flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center shrink-0">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading font-semibold text-sm leading-tight">Beaulon Assistant</p>
              <p className="text-[11px] text-brand-100 leading-tight mt-0.5 flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400"></span>
                Assistant IA · Disponible 24/7
              </p>
            </div>
            {messages.length > 0 && (
              <button
                type="button"
                onClick={resetSession}
                className="text-brand-100 hover:text-white text-xs flex items-center gap-1 px-2 py-1 rounded hover:bg-white/10"
                title="Recommencer une nouvelle conversation"
                data-testid="chat-reset"
              >
                <RotateCcw className="h-3 w-3" /> Nouv.
              </button>
            )}
          </header>

          {/* Medical disclaimer (always visible) */}
          <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 text-[11px] text-amber-900 flex items-start gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-700 mt-0.5 shrink-0" />
            <span>Cet assistant IA ne donne pas de conseil médical. <strong>Urgence vitale : 15 (SAMU)</strong>.</span>
          </div>

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-anthracite-100/30">
            {messages.length === 0 ? (
              <div className="text-center py-2">
                <div className="mx-auto h-14 w-14 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center mb-3">
                  <Sparkles className="h-7 w-7 text-white" />
                </div>
                <p className="text-sm font-heading font-semibold text-anthracite-950">Bonjour 👋</p>
                <p className="text-xs text-anthracite-700 mt-1 px-4">
                  Je suis l'assistant du Groupe Ambulances Beaulon. Posez-moi vos questions sur les transports sanitaires, la prise en charge Sécu ou la prise de RDV.
                </p>
                <div className="mt-4 space-y-1.5 px-2">
                  {SUGGESTIONS.map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => sendMessage(s)}
                      className="w-full text-left px-3 py-2 text-xs rounded-lg bg-white border border-border hover:border-brand-300 hover:bg-brand-50 text-anthracite-800 transition-colors"
                      data-testid="chat-suggestion"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m) => (
                <MessageBubble key={m.id} role={m.role} content={m.content} isError={m.isError} />
              ))
            )}
            {loading && (
              <div className="flex items-center gap-2 text-xs text-anthracite-700">
                <div className="h-7 w-7 rounded-full bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center">
                  <Sparkles className="h-3.5 w-3.5 text-white" />
                </div>
                <div className="rounded-2xl rounded-bl-sm bg-white border px-3 py-2">
                  <span className="inline-flex gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-brand-500 animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="h-1.5 w-1.5 rounded-full bg-brand-500 animate-bounce" style={{ animationDelay: '120ms' }}></span>
                    <span className="h-1.5 w-1.5 rounded-full bg-brand-500 animate-bounce" style={{ animationDelay: '240ms' }}></span>
                  </span>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Quick actions */}
          <div className="px-3 py-2 border-t bg-white flex gap-2">
            <a
              href="tel:0470427263"
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-xs font-medium px-3 py-2"
              data-testid="chat-quick-call"
            >
              <Phone className="h-3.5 w-3.5" /> Appeler
            </a>
            <a
              href="/devis"
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border bg-white hover:bg-anthracite-100 text-anthracite-900 text-xs font-medium px-3 py-2"
              data-testid="chat-quick-quote"
            >
              <FileText className="h-3.5 w-3.5" /> Devis
            </a>
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
            className="border-t bg-white px-3 py-3 flex items-end gap-2"
          >
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              rows={1}
              maxLength={2000}
              placeholder="Posez votre question…"
              className="flex-1 resize-none rounded-xl border border-anthracite-200 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200 px-3 py-2 text-sm placeholder:text-muted-foreground max-h-32"
              data-testid="chat-input"
              disabled={loading}
            />
            <button
              type="submit"
              disabled={!input.trim() || loading}
              className="h-10 w-10 rounded-xl bg-brand-500 hover:bg-brand-600 disabled:bg-anthracite-200 text-white flex items-center justify-center transition-colors shrink-0"
              aria-label="Envoyer"
              data-testid="chat-send"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}

function MessageBubble({ role, content, isError }) {
  const isUser = role === 'user';
  return (
    <div className={`flex items-start gap-2 ${isUser ? 'flex-row-reverse' : ''}`} data-testid={`chat-message-${role}`}>
      <div className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 ${
        isUser ? 'bg-anthracite-200 text-anthracite-700' : 'bg-gradient-to-br from-brand-500 to-brand-700 text-white'
      }`}>
        {isUser ? <span className="text-[11px] font-semibold">Vous</span> : <Sparkles className="h-3.5 w-3.5" />}
      </div>
      <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed ${
        isUser
          ? 'bg-brand-500 text-white rounded-br-sm'
          : isError
            ? 'bg-amber-50 text-amber-900 border border-amber-200 rounded-bl-sm'
            : 'bg-white border text-anthracite-900 rounded-bl-sm'
      }`}>
        <RichText text={content} />
      </div>
    </div>
  );
}

// Simple markdown-ish renderer: bold (**), links to tel: and /paths
function RichText({ text }) {
  // Replace **bold** with <strong>
  const parts = [];
  const regex = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\)|https?:\/\/\S+|tel:\d+|\/[a-z\-]+)/g;
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const seg = match[0];
    if (seg.startsWith('**') && seg.endsWith('**')) {
      parts.push(<strong key={`b-${key++}`}>{seg.slice(2, -2)}</strong>);
    } else if (seg.startsWith('[')) {
      const m = seg.match(/\[([^\]]+)\]\(([^)]+)\)/);
      if (m) parts.push(<a key={`l-${key++}`} href={m[2]} className="underline underline-offset-2 hover:text-brand-700" target="_blank" rel="noopener noreferrer">{m[1]}</a>);
    } else if (seg.startsWith('http')) {
      parts.push(<a key={`u-${key++}`} href={seg} className="underline underline-offset-2" target="_blank" rel="noopener noreferrer">{seg}</a>);
    } else if (seg.startsWith('tel:')) {
      parts.push(<a key={`t-${key++}`} href={seg} className="underline underline-offset-2 font-medium">{seg.replace('tel:', '')}</a>);
    } else if (seg.startsWith('/')) {
      parts.push(<a key={`p-${key++}`} href={seg} className="underline underline-offset-2 font-medium">{seg}</a>);
    } else {
      parts.push(seg);
    }
    lastIndex = match.index + seg.length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return <>{parts}</>;
}
